import { Box, SelectChangeEvent, Typography, styled } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AsyncAutocomplete, Option } from '../../../components/common/AsyncAutocomplete';
import Button from '../../../components/common/Button';
import FileUploadField from '../../../components/common/FileUploadField';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import { usePermissions } from '../../../context/PermissionContext';
import { useNotification } from '../../../hooks/useNotification';
import { isValidEmail } from '../../../utils/common';
import Notification from '../../../components/common/Notification';
import { APP_ROUTES } from '../../../constants/routes';
import { PAGE_IDS, PERMISSION_IDS } from '../../../constants/permissions';
import { applicantService } from '../../../services/applicant.service';
import { vacancyService } from '../../../services/vacancy.service';

const Subtitle = styled(Typography)({
  fontSize: '15px',
  marginBottom: '24px',
  textAlign: 'left',
  fontWeight: 500,
  color: '#0000008C',
  fontFamily: 'Inter',
});

const FormContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  marginBottom: '24px',
  width: '80%',
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '32px',
  width: '80%',
  padding: '0 12px'
});

export interface ApplicantLocationState {
  initialData?: {
    id?: number;
    vacancyType: string;
    vacancyCode: string;
    vacancy: string;
    source: string;
    sourceReferral: string;
    designation: string;
  };
}

type AddApplicantFormState = {
  vacancyType: string;
  vacancyCode: string;
  vacancy: string;
  source: string;
  sourceReferral: string;
  designation: Option | null;
};

const AddApplicant: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ApplicantLocationState | undefined;
  const initialData = state?.initialData;
  const [formData, setFormData] = useState<AddApplicantFormState>({
    vacancyType: initialData?.vacancyType || '',
    vacancyCode: initialData?.vacancyCode || '',
    vacancy: initialData?.vacancy || '',
    source: initialData?.source || '',
    sourceReferral: initialData?.sourceReferral || '',
    designation: initialData?.designation
      ? { value: '', label: initialData.designation, code: '' }
      : null,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [vacancies, setVacancies] = useState<{ id: number; vacancyName: string, vacancyCode: string, vacancyType: string }[]>([]);
  const filteredVacancies = formData.vacancyType
    ? vacancies.filter(v => v.vacancyType === formData.vacancyType)
    : vacancies;
  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    if (pageId !== PAGE_IDS.APPLICANT_ADD) {
      setPageId(PAGE_IDS.APPLICANT_ADD);
      return;
    }

    const fetchVacanciesAndDesignations = async () => {
      try {
        setLoading(true);
        const response = await vacancyService.getList();

        setVacancies(response.data.vacancies);
        if (initialData) {
          const selectedVacancy = response.data.vacancies.find(
            v => v.vacancyName === initialData?.vacancy || v.vacancyCode === initialData?.vacancyCode
          );
          if (selectedVacancy) {
            setFormData(prev => ({
              ...prev,
              vacancy: selectedVacancy.id.toString(),
              vacancyCode: selectedVacancy.id.toString(),
              vacancyType: selectedVacancy.vacancyType,
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching vacancies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVacanciesAndDesignations();
  }, [initialData, pageId, setPageId]);

  const handleLoadDesignations = async (query: string) => {
    const response = await vacancyService.searchDesignations(query, PAGE_IDS.APPLICANT_ADD);

    return response.data.data.map((item: { id: number; designation: string; designationCode: string }) => ({
      value: String(item.id),
      label: item.designation,
      code: item.designationCode,
    }));
  };

  const handleTextChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));

    if (field === 'sourceReferral') {
        setFieldErrors(prev => ({ ...prev, sourceReferral: '' }));
    }
  };

  const handleFileChange = (newFiles: File[] | null) => {
    setFileError('');
    if (!newFiles || newFiles.length === 0) {
      setFiles([]);
      return;
    }

    const allowedTypes = ['.pdf', '.docx', '.doc'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    // Validate each file
    for (const file of newFiles) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setFileError(`Please upload only PDF, DOCX, or DOC files. Invalid file: ${file.name}`);
        return;
      }

      if (file.size > maxSize) {
        setFileError(`File size should be less than 5MB. Large file: ${file.name}`);
        return;
      }
    }

    setFiles(newFiles);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Check required fields
    if (!formData.vacancyType) {
      errors.vacancyType = 'Vacancy Type is required';
      isValid = false;
    }
    if (!formData.vacancyCode) {
      errors.vacancyCode = 'Vacancy Code is required';
      isValid = false;
    }
    if (!formData.vacancy) {
      errors.vacancy = 'Vacancy is required';
      isValid = false;
    }
    if (!formData.source) {
      errors.source = 'Source is required';
      isValid = false;
    }
    const sourceReferralValue = formData.sourceReferral.trim();
    if (sourceReferralValue) {
      if (!isValidEmail(sourceReferralValue)) {
        errors.sourceReferral = 'Source Referral must be a valid email address';
        isValid = false;
      }
    }
    if (files.length === 0) {
      setFileError('Please upload at least one CV file');
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setSubmitError('');

      const formDataToSend = new FormData();
      formDataToSend.append('vacancyType', formData.vacancyType);
      formDataToSend.append('vacancyCode', vacancies.find(v => v.id === parseInt(formData.vacancyCode))?.vacancyCode || '');
      formDataToSend.append('vacancy', vacancies.find(v => v.id === parseInt(formData.vacancy))?.vacancyName || '');
      formDataToSend.append('source', formData.source);
      formDataToSend.append('sourceReferral', formData.sourceReferral);
      formDataToSend.append(
        'designation',
        typeof formData.designation?.code === 'string' ? formData.designation.code : '',
      );
      
      // Append all files
      files.forEach((file, index) => {
        formDataToSend.append(`file${index}`, file, file.name);
      });

      if (initialData?.id != null) {
        formDataToSend.append('linkedinId', String(initialData.id));
      }

      const response = await applicantService.addApplicant(formDataToSend);

      if (response.status === 200) {
        const responseData = response.data as {
          errorCount: number;
          successCount: number;
          totalFiles: number;
          results: Array<{
            fileIndex: number;
            fileName: string;
            message: string;
            status: string;
          }>;
        };
        
        // Show appropriate success message based on response
        if (responseData.errorCount === 0) {
          showNotification(
            `All ${responseData.successCount} CV(s) uploaded successfully and emails sent!`,
            'success',
            'Upload Successful'
          );
        } else if (responseData.successCount > 0) {
          showNotification(
            `${responseData.successCount} out of ${responseData.totalFiles} CV(s) uploaded successfully. ${responseData.errorCount} failed.`,
            'success',
            'Partial Success'
          );
        } else {
          showNotification(
            'All CV uploads failed. Please try again.',
            'error',
            'Upload Failed'
          );
        }

        // Navigate after a short delay to allow user to see the notification
        setTimeout(() => {
          navigate(APP_ROUTES.APPLICANT.SEARCH);
        }, 3000);
      }

    } catch (error: any) {
      console.error('Error adding applicant:', error);
      setSubmitError(error.response?.data?.message || 'Failed to add applicant. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Implement cancel functionality
    setFormData({
      vacancyType: '',
      vacancyCode: '',
      vacancy: '',
      source: '',
      sourceReferral: '',
      designation: null,
    });
    setFiles([]);
    setFileError('');
  };

  return (
    <Box className='page-container'>
      <Typography className='page-title'>Add Applicant</Typography>
      <Box className='edit-section'>
        <Subtitle>Please input the vacancy information and upload CVs for available positions</Subtitle>

        {submitError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {submitError}
          </Typography>
        )}

        {loading ?
          <Loading /> :
          <>
            <FormContainer>
              <SelectField
                label="Vacancy Type"
                required
                value={formData.vacancyType}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    vacancyType: event.target.value as string,
                    vacancyCode: '',
                    vacancy: '',
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancyType: '', vacancyCode: '', vacancy: '' }));
                }}
                error={!!fieldErrors.vacancyType}
                helperText={fieldErrors.vacancyType}
                options={[
                  { value: 'TAAS', label: 'TAAS' },
                  { value: 'EOR', label: 'EOR' },
                ]}
              />

              <SelectField
                label="Vacancy Code"
                required
                value={formData.vacancyCode}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  const selectedId = event.target.value as string;
                  const selectedVacancy = vacancies.find(v => v.id === parseInt(selectedId));
                  setFormData(prev => ({
                    ...prev,
                    vacancyCode: selectedId,
                    vacancy: selectedId,
                    vacancyType: selectedVacancy?.vacancyType || prev.vacancyType,
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancyCode: '', vacancy: '', vacancyType: '' }));
                }}
                error={!!fieldErrors.vacancyCode}
                helperText={fieldErrors.vacancyCode}
                options={loading ? [{ value: '', label: 'Loading...' }] : filteredVacancies.map((vacancy) => ({
                  value: vacancy.id.toString(),
                  label: vacancy.vacancyCode
                }))}
              />

              <SelectField
                label="Vacancy"
                required
                value={formData.vacancy}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  const selectedId = event.target.value as string;
                  const selectedVacancy = vacancies.find(v => v.id === parseInt(selectedId));
                  setFormData(prev => ({
                    ...prev,
                    vacancy: selectedId,
                    vacancyCode: selectedId,
                    vacancyType: selectedVacancy?.vacancyType || prev.vacancyType,
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancy: '', vacancyCode: '', vacancyType: '' }));
                }}
                error={!!fieldErrors.vacancy}
                helperText={fieldErrors.vacancy}
                options={loading ? [{ value: '', label: 'Loading...' }] : filteredVacancies.map((vacancy) => ({
                  value: vacancy.id.toString(),
                  label: vacancy.vacancyName
                }))}
              />

              <SelectField
                label="Source"
                required
                value={formData.source}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    source: event.target.value as string
                  }));
                  setFieldErrors(prev => ({ ...prev, source: '' }));
                }}
                error={!!fieldErrors.source}
                helperText={fieldErrors.source}
                options={[
                  { value: 'direct', label: 'Direct' },
                  { value: 'linkedin', label: 'Linkedin' },
                  { value: 'agency', label: 'Agency' },
                  { value: 'website', label: 'Website' },
                  { value: 'internal referral', label: 'Internal Referral' },
                  { value: 'external referral', label: 'External Referral' },
                ]}
              />

              <TextField
                id="sourceReferral"
                label="Source Referral"
                value={formData.sourceReferral}
                onChange={handleTextChange('sourceReferral')}
                error={!!fieldErrors.sourceReferral}
                helperText={fieldErrors.sourceReferral}
              />

              <AsyncAutocomplete
                label="Designation"
                value={formData.designation}
                onChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    designation: value
                  }));
                }}
                loadOptions={handleLoadDesignations}
              />

              <FileUploadField
                label="Upload CV (Pdf, Docx Or Doc Format)"
                required
                onChange={handleFileChange}
                error={fileError}
                width="174px"
                value={files}
                multiple={true}
              />
            </FormContainer>
          </>
        }
      </Box>
      {hasPermission(PERMISSION_IDS.APPLICANT_ADD) && (
        <ButtonContainer>
          <Button
            width='175px'
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            width='175px'
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            isLoading={saving}
          >
            {saving ? 'Saving...' : 'Submit'}
          </Button>
        </ButtonContainer>
      )}

      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default AddApplicant; 